import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function About() {
  useSEO({
    title: 'About Us | ProtectWize - Our Story & Mission',
    description: 'Learn about ProtectWize\'s mission to transform business intelligence. Founded by Giovani Ulrich, we provide cutting-edge data analytics solutions with enterprise-grade security and AI-powered insights.',
    keywords: 'about protectwize, company mission, data analytics company, business intelligence solutions, giovani ulrich',
    canonical: `${import.meta.env.VITE_SITE_URL}/about`,
    ogType: 'website',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'About ProtectWize',
        description: 'Learn about ProtectWize\'s mission to transform business intelligence through advanced data analytics and AI-powered insights.',
        url: `${import.meta.env.VITE_SITE_URL}/about`,
        mainEntity: {
          '@type': 'Organization',
          name: 'ProtectWize',
          url: import.meta.env.VITE_SITE_URL,
          foundingDate: '2021',
          founder: {
            '@type': 'Person',
            name: 'Giovani Ulrich',
            jobTitle: 'Founder & CEO'
          }
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Giovani Ulrich',
        jobTitle: 'Founder & CEO',
        worksFor: {
          '@type': 'Organization',
          name: 'ProtectWize'
        },
        alumniOf: {
          '@type': 'EducationalOrganization',
          name: 'Postgraduate in Cybersecurity'
        }
      }
    ]
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center pt-20"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/43c6433c3932d524f697c8e98d7eec7d.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-900/90 to-gray-950/85"></div>
        
        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-16 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-8">
              <i className="ri-building-line text-teal-400"></i>
              <span className="text-sm text-teal-300">Since 2021</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              About ProtectWize
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed">
              Empowering businesses with cutting-edge technology solutions and cybersecurity expertise since 2021.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Our Story
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Founded in 2021, ProtectWize emerged from a vision to provide comprehensive technology solutions that address the evolving challenges of modern businesses. We specialize in delivering expert consulting services, cloud infrastructure, advanced monitoring systems, data analysis, and robust cybersecurity solutions.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Our expertise spans across critical domains including web security, crypto market analysis, and blockchain technology. We understand the complexities of today's digital landscape and provide tailored solutions that protect and empower your business.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                With a focus on innovation and security-first approach, we help organizations navigate the digital transformation journey with confidence and resilience.
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20cybersecurity%20team%20working%20with%20multiple%20monitors%20displaying%20network%20security%20dashboards%2C%20data%20analytics%2C%20blockchain%20visualizations%2C%20cloud%20infrastructure%20diagrams%2C%20modern%20tech%20office%20environment%2C%20collaborative%20workspace%2C%20teal%20and%20cyan%20accent%20lighting%2C%20professional%20atmosphere&width=800&height=600&seq=about-team-001&orientation=landscape"
                alt="ProtectWize Team"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
              <div className="absolute -bottom-8 -left-8 bg-teal-500 rounded-2xl p-8 shadow-xl">
                <div className="text-4xl font-bold text-gray-900 mb-2">3+</div>
                <div className="text-gray-900 font-semibold">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our Core Services
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Consulting */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Consulting</h3>
              <p className="text-gray-400 leading-relaxed">
                Strategic technology consulting to help you make informed decisions and optimize your IT infrastructure for maximum efficiency and security.
              </p>
            </div>

            {/* Cloud Solutions */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-cloud-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Cloud Solutions</h3>
              <p className="text-gray-400 leading-relaxed">
                Comprehensive cloud infrastructure design, migration, and management services to ensure scalability, reliability, and cost-effectiveness.
              </p>
            </div>

            {/* Monitoring */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-radar-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Monitoring</h3>
              <p className="text-gray-400 leading-relaxed">
                Real-time monitoring and alerting systems to ensure your infrastructure runs smoothly and potential issues are detected before they impact your business.
              </p>
            </div>

            {/* Data Analysis */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-bar-chart-box-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Data Analysis</h3>
              <p className="text-gray-400 leading-relaxed">
                Advanced data analytics and business intelligence solutions to transform your data into actionable insights and drive strategic decision-making.
              </p>
            </div>

            {/* Cybersecurity */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Cybersecurity</h3>
              <p className="text-gray-400 leading-relaxed">
                Comprehensive cybersecurity solutions with emphasis on web security, protecting your digital assets from evolving threats and vulnerabilities.
              </p>
            </div>

            {/* Blockchain & Crypto */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-links-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Blockchain & Crypto</h3>
              <p className="text-gray-400 leading-relaxed">
                Specialized expertise in blockchain technology and crypto market analysis, helping you navigate the decentralized future with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://static.readdy.ai/image/f1a994e301941ad7da8a9075cff6d6e3/a16c4f41218b6a70d478c5436bf70680.png"
                alt="Founder"
                className="rounded-2xl shadow-2xl w-full h-[600px] object-cover object-top"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
                <i className="ri-user-star-line text-teal-400"></i>
                <span className="text-sm text-teal-300">Leadership</span>
              </div>
              
              <h2 className="text-4xl font-bold text-white mb-6">
                Meet Our Founder
              </h2>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                <strong className="text-teal-400">Giovani Ulrich</strong>, our founder, brings over <strong className="text-teal-400">20 years of experience in IT</strong> to ProtectWize. This extensive background in automation, cloud infrastructure, and cybersecurity forms the foundation of our company's technical excellence and innovative approach.
              </p>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Holding a <strong className="text-teal-400">Postgraduate degree in Cybersecurity</strong>, Giovani combines academic rigor with real-world experience to deliver solutions that are both theoretically sound and practically effective.
              </p>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                This unique blend of extensive industry experience and advanced academic credentials enables ProtectWize to provide cutting-edge solutions that address the most complex challenges in today's digital landscape.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="text-3xl font-bold text-teal-400 mb-2">20+</div>
                  <div className="text-gray-300 font-semibold">Years in IT</div>
                  <div className="text-gray-500 text-sm mt-1">Industry Experience</div>
                </div>
                
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="text-3xl font-bold text-teal-400 mb-2">
                    <i className="ri-graduation-cap-line"></i>
                  </div>
                  <div className="text-gray-300 font-semibold">Postgraduate</div>
                  <div className="text-gray-500 text-sm mt-1">Cybersecurity</div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <div className="px-4 py-2 bg-teal-500/10 rounded-lg border border-teal-500/30">
                  <span className="text-teal-400 font-semibold text-sm">Automation</span>
                </div>
                <div className="px-4 py-2 bg-teal-500/10 rounded-lg border border-teal-500/30">
                  <span className="text-teal-400 font-semibold text-sm">Cloud Infrastructure</span>
                </div>
                <div className="px-4 py-2 bg-teal-500/10 rounded-lg border border-teal-500/30">
                  <span className="text-teal-400 font-semibold text-sm">Cybersecurity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our Values
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Security First</h3>
              <p className="text-gray-400">
                We prioritize security in every solution we deliver, ensuring your data and systems are protected.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-flash-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Innovation</h3>
              <p className="text-gray-400">
                We stay ahead of technology trends to provide cutting-edge solutions for our clients.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Partnership</h3>
              <p className="text-gray-400">
                We work closely with our clients as trusted partners in their digital transformation journey.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-star-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Excellence</h3>
              <p className="text-gray-400">
                We are committed to delivering the highest quality solutions and exceptional service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Let's discuss how our expertise can help you achieve your technology goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/contact')}
              className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-xl"
            >
              Get in Touch
            </button>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="px-8 py-4 bg-transparent text-white rounded-lg font-semibold border-2 border-gray-600 hover:bg-gray-800/50 transition-all cursor-pointer whitespace-nowrap"
            >
              View Our Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}